import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, switchMap } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthService } from "./auth.service";

/**
 * Attaches the bearer token to API requests.
 *
 * Stays in the observable world the whole way down — `switchMap`, never `lastValueFrom`. Converting the
 * inner `next.handle(...)` to a promise silently makes every request in the app uncancellable: a promise
 * has no teardown, so unsubscribing stops you hearing the response but never reaches HttpXhrBackend, and
 * `xhr.abort()` is never called. That in turn means `httpResource`/`resource` superseding a request has no
 * effect, and the server keeps working on a response nobody will read.
 *
 * The token lookup itself is still a promise (MSAL's API) and cannot be cancelled — but it is short and
 * cached, and everything after it is cancellable, which is what matters.
 *
 * Token failures are AuthService's concern: it re-authenticates with a redirect when interaction is
 * required and rethrows anything else, which then surfaces through GlobalErrorInterceptor. This
 * interceptor never logs the user out.
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(
        private authService: AuthService
    ) { }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const isApiUrl = request.url.startsWith(environment.adminConfig.apiUri);
        if (!isApiUrl) {
            return next.handle(request);
        }

        return from(this.authService.getAccessToken()).pipe(
            switchMap(token => next.handle(request.clone({ setHeaders: { authorization: token } })))
        );
    }
}
